// One-click weekly payroll for Blackwood Barbers.
//
// Pick any date in the pay week, review every barber's hours / tips /
// bonus / wages, then click Create pay run. The server pushes timesheets
// to Xero, approves them, syncs per-employee pay rates, creates a DRAFT
// pay run, and materialises each barber's payslip with Tips and Bonus.
// The admin then opens Xero → Payroll → Pay Runs to review and POST
// (which files STP and triggers super).

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PayrollTabs } from '../components/PayrollTabs';
import { ResponsiveList, type ResponsiveListColumn } from '../components/ResponsiveList';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const kpiCardStyle: CSSProperties = {
  ...cardStyle,
  textAlign: 'center',
  flex: '1 1 160px',
  minWidth: 140,
};

const inputStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  minHeight: 44, // touch target — ui-ux-admin §1.2
};

const buttonPrimary: CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-5)',
  borderRadius: 'var(--radius-default)',
  border: 'none',
  backgroundColor: 'var(--color-accent)',
  color: '#1e1e1e',
  fontWeight: 700,
  fontSize: 'var(--font-size-base)',
  cursor: 'pointer',
  minHeight: 44,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 4,
};

function fmtMoney(n: number) {
  return n.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });
}

function brisbaneToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ });
}

/** A date 7 days ago in Brisbane — the default landing date so the page
 *  shows the most-recent CLOSED pay week (Mon-Sun ending last Sunday). */
function lastWeekDate(): string {
  const d = new Date(`${brisbaneToday()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString().slice(0, 10);
}

function formatDate(d: string) {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: BRISBANE_TZ,
  });
}

type PayrollRow = {
  barberId: number;
  barberName: string;
  employmentType: 'FT' | 'PT' | 'CASUAL';
  xeroEmployeeName: string | null;
  weekdayHours: number;
  saturdayHours: number;
  weekdayRate: number;
  saturdayRate: number;
  ordinaryEarnings: number;
  tips: number;
  serviceRevenue: number;
  bonusRate: number;
  bonusAmount: number;
  days: { date: string; dow: number; hours: number }[];
};

export function PayrollRun() {
  const queryClient = useQueryClient();
  const [dateInWeek, setDateInWeek] = useState<string>(lastWeekDate());
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<{
    steps: { step: string; ok: boolean; error?: string }[];
    payRunXeroId: string | null;
    payRunStatus: string | null;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showRunDetails, setShowRunDetails] = useState(false);

  const { data: status } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => api.xeroStatus(),
  });
  const tenants = useMemo(() => status?.tenants ?? [], [status]);
  const tenantId = tenants[0]?.tenantId;

  const { data, isLoading, error } = useQuery({
    queryKey: ['weekly-payroll', dateInWeek, tenantId],
    queryFn: () => api.payrollWeekly(dateInWeek, tenantId),
    enabled: Boolean(dateInWeek),
  });

  // Reset the run-result banner when the date changes.
  useEffect(() => {
    setRunResult(null);
    setShowRunDetails(false);
  }, [dateInWeek]);

  function handlePayClick() {
    if (!tenantId) return;
    setConfirmOpen(true);
  }

  async function performPush() {
    setConfirmOpen(false);
    if (!tenantId) return;
    setRunning(true);
    setRunResult(null);
    try {
      const res = await api.payrollRunWeekly({ dateInWeek, tenantId });
      setRunResult({
        steps: res.steps,
        payRunXeroId: res.payRunXeroId,
        payRunStatus: res.payRunStatus,
      });
      queryClient.invalidateQueries({ queryKey: ['weekly-payroll', dateInWeek, tenantId] });
    } finally {
      setRunning(false);
    }
  }

  const rows: PayrollRow[] = data?.rows ?? [];
  const totals = data?.totals;
  const existingPayRun = data?.existingPayRun ?? null;
  // POSTED pay runs are read-only — block re-running for the same week.
  // We trust the local cache here; if it's stale (admin POSTed via Xero UI)
  // the backend still rejects on the actual createDraftPayRun call.
  const periodIsPosted = existingPayRun?.status === 'POSTED';

  const columns: ResponsiveListColumn<PayrollRow>[] = [
    {
      key: 'barber',
      label: 'Barber',
      primaryOnMobile: true,
      render: (r) => <BarberCell row={r} />,
    },
    {
      key: 'type',
      label: 'Type',
      hideOnMobile: true,
      render: (r) => (
        <span
          title={`weekday $${r.weekdayRate.toFixed(2)}/hr · Sat $${r.saturdayRate.toFixed(2)}/hr`}
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 999,
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: 'var(--color-text-muted)',
          }}
        >
          {r.employmentType}
        </span>
      ),
    },
    {
      key: 'wk_hrs',
      label: 'Wk hrs',
      align: 'right',
      render: (r) => (
        <>
          {r.weekdayHours.toFixed(1)}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            @ {fmtMoney(r.weekdayRate)}
          </div>
        </>
      ),
    },
    {
      key: 'sat_hrs',
      label: 'Sat hrs',
      align: 'right',
      render: (r) =>
        r.saturdayHours > 0 ? (
          <>
            <span style={{ color: '#d6932a', fontWeight: 600 }}>{r.saturdayHours.toFixed(1)}</span>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              @ {fmtMoney(r.saturdayRate)}
            </div>
          </>
        ) : (
          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
        ),
    },
    {
      key: 'wages',
      label: 'Wages',
      align: 'right',
      render: (r) => <strong>{fmtMoney(r.ordinaryEarnings)}</strong>,
    },
    {
      key: 'services',
      label: 'Services',
      align: 'right',
      render: (r) => fmtMoney(r.serviceRevenue),
    },
    {
      key: 'bonus',
      label: 'Bonus',
      align: 'right',
      render: (r) =>
        r.bonusAmount > 0 ? (
          <>
            <span style={{ color: '#2f7d5b', fontWeight: 600 }}>{fmtMoney(r.bonusAmount)}</span>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              {(r.bonusRate * 100).toFixed(0)}%
            </div>
          </>
        ) : (
          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
        ),
    },
    {
      key: 'tips',
      label: 'Tips',
      align: 'right',
      render: (r) =>
        r.tips > 0 ? (
          <span style={{ color: '#2f7d5b', fontWeight: 600 }}>{fmtMoney(r.tips)}</span>
        ) : (
          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
        ),
    },
  ];

  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        maxWidth: 1300,
      }}
    >
      <div>
        <h1 style={{ margin: 0, marginBottom: 4 }}>Payroll · Weekly run</h1>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Pick any date in the pay week to review every barber's hours, tips and bonus.{' '}
          <strong>Create pay run</strong> pushes timesheets to Xero, syncs rates, materialises each
          payslip with Tips and Bonus, and leaves a DRAFT pay run ready for review. Open Xero →
          Payroll → Pay Runs to post it (files STP, releases payment, triggers super).
        </div>
      </div>

      <PayrollTabs />

      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-4)',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <label style={labelStyle}>Pay week (any date)</label>
            <input
              type="date"
              style={inputStyle}
              value={dateInWeek}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setDateInWeek(v);
              }}
            />
          </div>
          {data && (
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              <div>
                Period: <strong>{formatDate(data.startDate)}</strong> →{' '}
                <strong>{formatDate(data.endDate)}</strong>
              </div>
              <div>
                Payment date: <strong>{formatDate(data.paymentDate)}</strong>
              </div>
            </div>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <button
              style={{
                ...buttonPrimary,
                opacity: running || !tenantId || rows.length === 0 || periodIsPosted ? 0.5 : 1,
                cursor:
                  running || !tenantId || rows.length === 0 || periodIsPosted
                    ? 'not-allowed'
                    : 'pointer',
              }}
              onClick={handlePayClick}
              disabled={running || !tenantId || rows.length === 0 || periodIsPosted}
              title={
                !tenantId
                  ? 'Connect Xero first'
                  : rows.length === 0
                    ? 'No barbers'
                    : periodIsPosted
                      ? 'Pay run already posted for this week'
                      : 'Pushes timesheets, syncs rates, and creates a DRAFT pay run in Xero'
              }
            >
              {running ? 'Creating…' : 'Create pay run'}
            </button>
          </div>
        </div>
      </div>

      {periodIsPosted && (
        <div
          style={{
            ...cardStyle,
            border: '1px solid #48bb78',
            backgroundColor: 'rgba(72, 187, 120, 0.08)',
            color: '#2f855a',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <strong>Pay run already posted</strong> for this week (Xero ID{' '}
          <code>{existingPayRun?.xeroPayRunId.slice(0, 8)}…</code>). Posted runs are read-only — STP
          is filed and super has been triggered. Pick a different week to create another.
        </div>
      )}

      {totals && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
          }}
        >
          <div style={kpiCardStyle}>
            <div style={labelStyle}>Hours</div>
            <div
              style={{
                fontSize: 'var(--font-size-xl)',
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 700,
              }}
            >
              {(totals.weekdayHours + totals.saturdayHours + totals.sundayHours).toFixed(1)}h
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              {totals.weekdayHours}h wk · {totals.saturdayHours}h sat
              {totals.sundayHours > 0 ? ` · ${totals.sundayHours}h sun` : ''}
            </div>
          </div>
          <div style={kpiCardStyle}>
            <div style={labelStyle}>Wages</div>
            <div
              style={{
                fontSize: 'var(--font-size-xl)',
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 700,
              }}
            >
              {fmtMoney(totals.ordinaryEarnings)}
            </div>
          </div>
          <div style={kpiCardStyle}>
            <div style={labelStyle}>Bonus</div>
            <div
              style={{
                fontSize: 'var(--font-size-xl)',
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 700,
              }}
            >
              {fmtMoney(totals.bonusAmount)}
            </div>
          </div>
          <div style={kpiCardStyle}>
            <div style={labelStyle}>Tips</div>
            <div
              style={{
                fontSize: 'var(--font-size-xl)',
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 700,
              }}
            >
              {fmtMoney(totals.tips)}
            </div>
          </div>
        </div>
      )}

      {runResult && (
        <RunResultPanel
          runResult={runResult}
          showDetails={showRunDetails}
          onToggle={() => setShowRunDetails((s) => !s)}
        />
      )}

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Per barber</h2>
        {isLoading ? (
          <div>Loading…</div>
        ) : error ? (
          <div style={{ color: 'var(--color-danger, #e53e3e)' }}>
            Could not load payroll: {error instanceof Error ? error.message : 'unknown error'}
          </div>
        ) : (
          <ResponsiveList
            rows={rows}
            getRowKey={(r) => String(r.barberId)}
            columns={columns}
            emptyMessage="No barbers in this period."
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Create pay run in Xero?"
        message="This pushes timesheets, syncs per-employee pay rates, creates a DRAFT pay run, and materialises each barber's payslip with Tips and Bonus. After this, review the pay run in Xero (Payroll → Pay Runs) and click Post to file STP, release payment, and trigger super."
        tone="warning"
        confirmLabel="Create pay run"
        cancelLabel="Cancel"
        onConfirm={performPush}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

/**
 * Per-barber primary cell — name + Xero mapping + the Mon-Sat day pills.
 * Pulled out so the same render works inside ResponsiveList's desktop
 * table cell and its mobile card title slot.
 */
function BarberCell({ row }: { row: PayrollRow }) {
  return (
    <>
      <strong>{row.barberName}</strong>
      {row.xeroEmployeeName ? (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            marginTop: 2,
          }}
        >
          Xero: {row.xeroEmployeeName}
        </div>
      ) : (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: '#ed8936',
            marginTop: 2,
          }}
        >
          Not mapped to Xero
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: 4,
          marginTop: 6,
          flexWrap: 'wrap',
        }}
      >
        {row.days
          .filter((d) => d.dow !== 0)
          .map((d) => {
            const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const isSat = d.dow === 6;
            const worked = d.hours > 0;
            return (
              <div
                key={d.date}
                title={`${d.date} · ${d.hours.toFixed(1)}h`}
                style={{
                  fontSize: 'var(--font-size-xs)',
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: '1px solid var(--color-border)',
                  backgroundColor: worked
                    ? isSat
                      ? 'rgba(214, 147, 42, 0.12)'
                      : 'var(--color-background)'
                    : 'transparent',
                  color: worked
                    ? isSat
                      ? '#d6932a'
                      : 'var(--color-text)'
                    : 'var(--color-text-muted)',
                  minWidth: 44,
                  textAlign: 'center',
                  fontWeight: worked ? 600 : 400,
                }}
              >
                <span style={{ opacity: 0.7 }}>{dayLabels[d.dow]}</span>{' '}
                {worked ? `${d.hours.toFixed(1)}h` : '—'}
              </div>
            );
          })}
      </div>
    </>
  );
}

/**
 * Result banner shown after a Create-pay-run click.
 *
 * On full success we keep the visible payload tight — one headline +
 * Xero ID + a "Show details" toggle if the admin wants the full step
 * list. On any failure we show the failed steps inline so the admin
 * can act without expanding anything.
 */
function RunResultPanel(props: {
  runResult: {
    steps: { step: string; ok: boolean; error?: string }[];
    payRunXeroId: string | null;
    payRunStatus: string | null;
  };
  showDetails: boolean;
  onToggle: () => void;
}) {
  const { runResult } = props;
  const allOk = runResult.steps.every((s) => s.ok);
  const failed = runResult.steps.filter((s) => !s.ok);
  const okCount = runResult.steps.length - failed.length;

  return (
    <div
      style={{
        ...cardStyle,
        border: `1px solid ${allOk ? '#48bb78' : '#ed8936'}`,
        backgroundColor: allOk ? 'rgba(72, 187, 120, 0.08)' : 'rgba(237, 137, 54, 0.08)',
      }}
    >
      <h3 style={{ margin: 0, marginBottom: 'var(--spacing-2)' }}>
        {allOk ? 'Pay run created in Xero' : 'Pay run finished with issues'}
      </h3>
      {runResult.payRunXeroId && (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--spacing-2)',
          }}
        >
          Xero pay run <code>{runResult.payRunXeroId.slice(0, 8)}…</code> · status{' '}
          <strong>{runResult.payRunStatus}</strong>. Open Xero → Payroll → Pay Runs to review and
          post.
        </div>
      )}
      {allOk ? (
        <>
          <div style={{ fontSize: 'var(--font-size-sm)', color: '#2f855a' }}>
            ✓ {okCount} steps completed.
          </div>
          <button
            type="button"
            onClick={props.onToggle}
            style={{
              marginTop: 'var(--spacing-2)',
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-xs)',
              padding: 0,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {props.showDetails ? 'Hide details' : 'Show details'}
          </button>
          {props.showDetails && (
            <ul
              style={{
                margin: 'var(--spacing-2) 0 0',
                paddingLeft: 20,
                fontSize: 'var(--font-size-sm)',
                color: '#2f855a',
              }}
            >
              {runResult.steps.map((s, i) => (
                <li key={i}>{s.step}</li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          {okCount > 0 && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              ✓ {okCount} step{okCount === 1 ? '' : 's'} completed before the failure
              {failed.length > 1 ? 's' : ''}.
            </div>
          )}
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 'var(--font-size-sm)' }}>
            {failed.map((s, i) => (
              <li key={i} style={{ color: 'var(--color-danger, #e53e3e)' }}>
                ✗ {s.step}
                {s.error ? ` — ${s.error}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
